"use client"

import { useEffect, useState } from "react"
import { useLocale, useTranslations } from "next-intl"
import Image from "next/image"
import { Clock, CheckCircle2, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { isProbablyVideoUrl } from "@/lib/gallery-media"
import { ListingVideoThumb } from "@/components/listings/listing-video-thumb"
import { ImageGalleryModal, type GalleryImage } from "@/components/chat/image-gallery-modal"

interface ProductionUpdate {
  id: string
  title: string
  body: string
  media_urls: string[]
  created_at: string
}

interface PreorderData {
  preorder_status: "open" | "funded" | "in_production" | "shipped" | null
  min_orders: number | null
  max_orders: number | null
  estimated_delivery: string | null
  paid_count: number
  updates: ProductionUpdate[]
}

interface Props {
  listingId: string
  className?: string
}

function renderMarkdown(md: string): string {
  const escaped = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")

  return escaped
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(
      /\[([^\]]+)\]\((https?:[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline">$1</a>'
    )
    .replace(/\n{2,}/g, "</p><p>")
    .replace(/\n/g, "<br />")
    .replace(/^([\s\S]*)$/, "<p>$1</p>")
}

function formatDate(value: string | null | undefined, locale: string, withTime = false): string {
  if (!value) return "—"
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return "—"
    return d.toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: withTime ? "2-digit" : undefined,
      minute: withTime ? "2-digit" : undefined,
    })
  } catch {
    return "—"
  }
}

export function PreorderTimelineSection({ listingId, className }: Props) {
  const t = useTranslations("preorder.timeline")
  const locale = useLocale()
  const [data, setData] = useState<PreorderData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [galleryState, setGalleryState] = useState<{
    images: GalleryImage[]
    initialIndex: number
    open: boolean
  }>({ images: [], initialIndex: 0, open: false })

  const openGallery = (update: ProductionUpdate, startIndex: number) => {
    const images: GalleryImage[] = update.media_urls.map((url, idx) => ({
      id: `${update.id}-${idx}`,
      url,
      alt: `${update.title} — ${idx + 1}`,
    }))
    setGalleryState({
      images,
      initialIndex: Math.max(0, Math.min(startIndex, images.length - 1)),
      open: true,
    })
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const res = await fetch(`/api/listings/${listingId}/preorder-timeline`, { cache: "no-store" })
        if (!res.ok) throw new Error("Failed to load pre-order data")
        const json = (await res.json()) as PreorderData
        if (!cancelled) setData(json)
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Failed to load")
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [listingId])

  if (loading) {
    return (
      <div className={cn("flex items-center gap-2 text-sm text-muted-foreground py-6", className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("loading")}
      </div>
    )
  }
  if (error || !data) return null

  return (
    <section className={cn("border-t pt-6 mt-6 space-y-6", className)} aria-labelledby="preorder-section-title">
      <div>
        <h2 id="preorder-section-title" className="text-lg font-semibold mb-3">{t("sectionTitle")}</h2>
        <ProgressBar data={data} />
      </div>

      <div>
        <h3 className="text-base font-semibold mb-4">{t("updatesTitle")}</h3>
        {data.updates.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("noUpdates")}</p>
        ) : (
          <ol className="relative border-l-2 border-muted pl-5 space-y-6">
            {data.updates.map((u) => (
              <li key={u.id} className="relative">
                <span className="absolute -left-[1.6rem] top-1.5 h-3 w-3 rounded-full bg-foreground ring-4 ring-background" />
                <time className="text-xs uppercase tracking-wide text-muted-foreground">
                  {formatDate(u.created_at, locale)}
                </time>
                <h4 className="text-sm font-semibold mt-1">{u.title}</h4>
                <div
                  className="prose prose-sm dark:prose-invert max-w-none mt-2 text-sm"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(u.body) }}
                />
                {u.media_urls.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {u.media_urls.map((url, idx) => (
                      <button
                        key={`${u.id}-m-${idx}`}
                        type="button"
                        onClick={() => openGallery(u, idx)}
                        className="relative h-24 w-24 overflow-hidden rounded-lg border bg-muted transition-opacity hover:opacity-90"
                        aria-label={t("openMediaAria")}
                      >
                        {isProbablyVideoUrl(url) ? (
                          <ListingVideoThumb src={url} />
                        ) : (
                          <Image
                            src={url}
                            alt=""
                            fill
                            sizes="96px"
                            className="object-cover"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>

      <ImageGalleryModal
        images={galleryState.images}
        initialIndex={galleryState.initialIndex}
        isOpen={galleryState.open}
        onCloseAction={() => setGalleryState((s) => ({ ...s, open: false }))}
        restoreScrollOnClose={false}
      />
    </section>
  )
}

function ProgressBar({ data }: { data: PreorderData }) {
  const t = useTranslations("preorder.timeline")
  const tStatus = useTranslations("preorder.status")
  const { min_orders, max_orders, paid_count, preorder_status } = data
  const status = preorder_status ?? "open"

  const hasMin = min_orders != null && min_orders > 0
  const hasMax = max_orders != null && max_orders > 0
  // A preorder without a production threshold (no min_orders) has no funding
  // gate, so there is no meaningful "progress toward a threshold". Only show a
  // bar when production has started (status advanced) or there is a min/max to
  // measure against — otherwise the bar would sit stuck at 0% forever.
  const showBar = status !== "open" || hasMin || hasMax

  const pct = (() => {
    if (status !== "open") return 100
    if (hasMin) return Math.min(100, Math.round((paid_count / min_orders!) * 100))
    if (hasMax) return Math.min(100, Math.round((paid_count / max_orders!) * 100))
    return 0
  })()

  const remaining = hasMin ? Math.max(0, min_orders! - paid_count) : 0

  const statusLabel = (() => {
    switch (status) {
      case "funded":
        return tStatus("funded")
      case "in_production":
        return tStatus("inProduction")
      case "shipped":
        return tStatus("shipped")
      default:
        return tStatus("open")
    }
  })()

  const capacityLine = (() => {
    if (max_orders == null) return null
    if (paid_count < max_orders) {
      return t("capacityWithSpotsLeft", {
        count: paid_count,
        max: max_orders,
        n: max_orders - paid_count,
      })
    }
    return t("capacitySoldOut", { count: paid_count, max: max_orders })
  })()

  return (
    <div className="space-y-2">
      {showBar && (
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full transition-all",
              status === "open" ? "bg-amber-500" : "bg-emerald-500"
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        {status === "open" ? (
          hasMin ? (
            <>
              <span className="inline-flex items-center gap-1 font-medium">
                <Clock className="h-4 w-4" />
                {t("ordersProgress", { count: paid_count, min: min_orders! })}
              </span>
              <span className="text-muted-foreground">
                {remaining > 0 ? t("moreToStart", { n: remaining }) : t("thresholdReached")}
              </span>
            </>
          ) : (
            <span className="inline-flex items-center gap-1 font-medium">
              <Clock className="h-4 w-4" />
              {t("ordersPlaced", { count: paid_count })}
            </span>
          )
        ) : (
          <span className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-4 w-4" />
            {t("thresholdReachedWithCount", { count: paid_count })}
          </span>
        )}
        {capacityLine && <span className="text-muted-foreground">{capacityLine}</span>}
      </div>
      {status !== "open" && (
        <p className="text-xs text-muted-foreground">
          {t("statusLabel")}:{" "}
          <span className="font-semibold uppercase tracking-wide">{statusLabel}</span>
        </p>
      )}
    </div>
  )
}
